<template>
  <div class="printing-page" :class="{ 'printing-page-create': isFormTab }">
    <CreateOrderPanel
      v-if="createMounted"
      v-show="isFormTab"
      :channel-code="selectedChannel"
      :channels="channels"
      :mode="formMode"
      :order-id="editingOrderId"
      :order-detail="editingOrderDetail"
      @saved="onFormSaved"
      @closed="goOrderList"
    />

    <template v-if="listMounted">
      <div v-show="!isFormTab" class="t6-list-page">
        <div class="t6-channel-bar">
          <div class="channel-left">
            <Select v-model="selectedChannel" placeholder="请选择渠道" style="width:240px" @on-change="onChannelChange">
              <Option v-for="ch in channels" :key="ch.channelCode" :value="ch.channelCode">
                {{ ch.channelName }}
              </Option>
            </Select>
            <Tag v-if="channelStatus === 'active'" color="green">已连接</Tag>
            <Tag v-else-if="channelStatus === 'inactive'" color="default">未登录</Tag>
            <Tag v-else-if="channelStatus === 'loading'" color="blue">连接中...</Tag>
            <span class="channel-name" v-if="selectedChannelName">{{ selectedChannelName }}</span>
          </div>
          <div class="channel-right">
            <span class="sync-time" v-if="orders.length">当前页 {{ orders.length }} / {{ pagination.total }} 条</span>
            <Button size="small" icon="md-refresh" :loading="ordersLoading" :disabled="!selectedChannel" @click="refreshAll">
              刷新
            </Button>
          </div>
        </div>

        <div class="status-strip" v-if="selectedChannel">
          <button
            v-for="item in statusFilters"
            :key="item.key || 'all'"
            class="status-pill"
            :class="{ active: selectedStatus === item.key }"
            type="button"
            @click="selectStatus(item.key)"
          >
            <span>{{ item.label }}</span>
            <b>{{ stats[item.statKey] || 0 }}</b>
          </button>
        </div>

        <div class="t6-toolbar" v-if="selectedChannel">
          <div class="toolbar-left">
            <Button size="small" type="primary" icon="md-add" @click="openCreateOrder">增加</Button>

            <Dropdown trigger="click" @on-click="handleToolbar">
              <Button size="small" type="primary">
                导入 <Icon type="ios-arrow-down" />
              </Button>
              <DropdownMenu slot="list">
                <DropdownItem name="import"><Icon type="ios-cloud-upload" /> 运单导入</DropdownItem>
                <DropdownItem name="import-modify"><Icon type="ios-create-outline" /> 导入修改</DropdownItem>
                <DropdownItem name="import-weight"><Icon type="md-speedometer" /> 导入重量</DropdownItem>
              </DropdownMenu>
            </Dropdown>

            <Dropdown trigger="click" @on-click="handleToolbar">
              <Button size="small" type="primary">
                打印 <Icon type="ios-arrow-down" />
              </Button>
              <DropdownMenu slot="list">
                <DropdownItem
                  v-for="action in availablePrintActions"
                  :key="action.value"
                  :name="'print-' + action.value"
                  :disabled="!hasSelection"
                >
                  <Icon :type="action.icon" /> {{ action.label }}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>

            <Dropdown trigger="click" @on-click="handleToolbar">
              <Button size="small" type="primary">
                导出 <Icon type="ios-arrow-down" />
              </Button>
              <DropdownMenu slot="list">
                <DropdownItem name="export-selected" :disabled="!hasSelection"><Icon type="md-download" /> 勾选导出</DropdownItem>
                <DropdownItem name="export-filtered"><Icon type="ios-download-outline" /> 当前筛选导出</DropdownItem>
              </DropdownMenu>
            </Dropdown>

            <Dropdown trigger="click" @on-click="handleToolbar">
              <Button size="small" type="primary">
                运单操作 <Icon type="ios-arrow-down" />
              </Button>
              <DropdownMenu slot="list">
                <DropdownItem name="copy" :disabled="!hasSelection"><Icon type="ios-copy-outline" /> 复制运单</DropdownItem>
                <DropdownItem name="delete" :disabled="!hasSelection"><Icon type="ios-trash-outline" /> 删除本地缓存</DropdownItem>
              </DropdownMenu>
            </Dropdown>

            <Button size="small" type="primary" icon="md-sync" :loading="syncLoading" @click="handleSync">
              同步平台订单
            </Button>
          </div>

          <div class="toolbar-right">
            <span class="selection-info" v-if="hasSelection">{{ selectionSummary }}</span>
            <Input
              v-model="searchQuery"
              class="quick-search"
              search
              clearable
              placeholder="运单号/客户参考号/转单号，最多10个"
              @on-search="searchOrders"
              @on-clear="searchOrders"
            />
            <Poptip placement="bottom-end" trigger="click" width="360">
              <Button size="small" icon="md-options" title="设置显示字段"></Button>
              <div slot="content" class="column-pop">
                <div class="column-pop-title">
                  <span>设置显示字段</span>
                  <Button size="small" type="text" @click="resetColumns">恢复默认</Button>
                </div>
                <CheckboxGroup v-model="visibleColumnKeys">
                  <div class="column-check-grid">
                    <Checkbox v-for="col in configurableColumns" :key="col.key" :label="col.key">
                      {{ col.title }}
                    </Checkbox>
                  </div>
                </CheckboxGroup>
              </div>
            </Poptip>
            <Button size="small" icon="md-funnel" @click="showFilter = !showFilter">筛选</Button>
            <Button v-if="hasActiveFilter" size="small" type="error" ghost icon="ios-close-circle-outline" @click="resetFilters">
              清除
            </Button>
          </div>
        </div>

        <div class="filter-panel" v-if="showFilter && selectedChannel">
          <Select v-model="dateType" style="width:130px" @on-change="loadOrders">
            <Option v-for="item in dateTypes" :key="item.value" :value="item.value">{{ item.label }}</Option>
          </Select>
          <DatePicker
            v-model="filterDateRange"
            type="daterange"
            placeholder="日期范围"
            style="width:240px"
            @on-change="loadOrders"
          />
          <Select v-model="filterDestination" placeholder="目的地" clearable style="width:150px" @on-change="loadOrders">
            <Option v-for="d in destinations" :key="d.value" :value="d.value">{{ d.label }}</Option>
          </Select>
          <Select v-model="filterCargoType" placeholder="货物类型" clearable style="width:150px" @on-change="loadOrders">
            <Option v-for="g in cargoTypes" :key="g.value" :value="g.value">{{ g.label }}</Option>
          </Select>
          <Button size="small" type="primary" @click="loadOrders">查询</Button>
          <Button size="small" @click="resetFilters">重置</Button>
        </div>

        <div class="table-shell" v-if="selectedChannel" ref="tableShell">
          <Table
            ref="ordersTable"
            :columns="tableColumns"
            :data="visibleOrders"
            :height="tableHeight"
            :loading="ordersLoading || !tableReady"
            :row-class-name="rowClassName"
            border
            stripe
            size="small"
            @on-selection-change="onSelectionChange"
            @on-sort-change="onSortChange"
            @on-row-dblclick="viewOrder"
          />
          <div class="table-footer" ref="tableFooter">
            <div class="cal-info">{{ pageSummary }}</div>
            <Page
              :total="pagination.total"
              :current="pagination.page"
              :page-size="pagination.pageSize"
              :page-size-opts="[20, 50, 100, 200, 500]"
              show-total
              show-sizer
              @on-change="onPageChange"
              @on-page-size-change="onPageSizeChange"
            />
          </div>
        </div>

        <div class="empty-state" v-if="!selectedChannel">
          <Icon type="md-print" size="48" color="#bcc5d0" />
          <p>请选择渠道开始管理 T6 运单</p>
        </div>
      </div>

      <Drawer
        v-model="detailDrawerVisible"
        :title="detailTitle"
        width="57%"
        :mask-closable="false"
        class="order-detail-drawer"
      >
        <div class="detail-drawer-body">
          <Spin v-if="detailLoading" fix />
          <Alert
            v-if="currentOrderDetail && currentOrderDetail.detailLoadWarning"
            type="warning"
            show-icon
            style="margin-bottom:12px"
          >
            {{ currentOrderDetail.detailLoadWarning }}
          </Alert>

          <Tabs v-model="detailTab">
            <TabPane label="基本信息" name="basic">
              <div class="detail-summary-line" v-if="currentOrderDetail">
                <Tag :color="statusColor(detailStatus)">{{ statusLabel(detailStatus) }}</Tag>
                <span>客户参考号：{{ displayValue(detailSummary.customerNumber) }}</span>
                <span>系统单号：{{ displayValue(detailSummary.systemNumber) }}</span>
                <span>运单号：{{ displayValue(detailSummary.waybillNumber) }}</span>
              </div>

              <div class="detail-groups">
                <div v-for="group in detailBasicGroups" :key="group.title" class="detail-group">
                  <h4>{{ group.title }}</h4>
                  <div class="detail-grid">
                    <div v-for="item in group.items" :key="item.label" class="detail-item">
                      <label>{{ item.label }}</label>
                      <span>{{ displayValue(item.value) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabPane>

            <TabPane label="材积信息" name="dimensions">
              <Table :columns="detailDimensionColumns" :data="detailDimensions" border size="small" />
            </TabPane>

            <TabPane label="物品信息" name="goods">
              <Table
                v-if="detailGoods.length"
                :columns="detailGoodsColumns"
                :data="detailGoods"
                border
                size="small"
              />
              <div v-else class="detail-empty">暂无物品信息</div>
            </TabPane>

            <TabPane label="附件" name="attachments">
              <Table
                v-if="detailAttachments.length"
                :columns="detailAttachmentColumns"
                :data="detailAttachments"
                border
                size="small"
              />
              <div v-else class="detail-empty">暂无附件</div>
            </TabPane>

            <TabPane label="轨迹" name="tracks">
              <Table
                v-if="detailTracks.length"
                :columns="detailTrackColumns"
                :data="detailTracks"
                border
                size="small"
              />
              <div v-else class="detail-empty">暂无轨迹</div>
            </TabPane>
          </Tabs>
        </div>

        <div class="detail-footer">
          <Button type="primary" :disabled="!canEditCurrentDetail" @click="startEditFromDetail">修改</Button>
          <Button @click="handleExcelModify">修改(Excel)</Button>
          <Button @click="detailDrawerVisible = false">关闭</Button>
        </div>
      </Drawer>

      <Modal v-model="printModalVisible" title="打印运单" width="520" :footer-hide="true">
        <div class="print-options">
          <div class="print-row">
            <label>打印内容</label>
            <Select v-model="printAction" style="width:100%">
              <Option v-for="action in availablePrintActions" :key="action.value" :value="action.value">
                {{ action.label }}
              </Option>
            </Select>
          </div>
          <div class="print-row">
            <label>打印方式</label>
            <RadioGroup v-model="printDelivery" type="button">
              <Radio label="preview">浏览器预览</Radio>
              <Radio label="silent" :disabled="selectedPrintActionConfig && !selectedPrintActionConfig.silent">本机静默打印</Radio>
            </RadioGroup>
          </div>
          <div v-if="printDelivery === 'silent'" class="silent-settings">
            <div class="print-row">
              <label>打印机名称</label>
              <Input v-model.trim="silentPrinterName" placeholder="请输入本机打印插件中的打印机名称" />
            </div>
            <div class="print-row compact">
              <label>打印份数</label>
              <InputNumber v-model="silentCopies" :min="1" :max="20" />
              <Checkbox v-model="silentFallbackPreview">失败后打开浏览器预览</Checkbox>
            </div>
          </div>
          <Alert v-if="printDelivery === 'silent'" type="info" show-icon>
            静默打印需要本机运行 T6 打印插件（localhost:8866），且当前打印内容必须返回 PDF。
          </Alert>
          <Alert v-else-if="selectedPrintActionConfig && !selectedPrintActionConfig.silent" type="warning" show-icon>
            当前打印内容仅支持浏览器预览。
          </Alert>
          <div class="print-summary">
            已选 {{ printPkids.length }} 单，{{ printActionLabel }}，{{ printDelivery === 'silent' ? '本机静默打印' : '浏览器预览' }}
          </div>
          <div class="print-actions">
            <Button @click="printModalVisible = false">取消</Button>
            <Button type="primary" :loading="printChecking" @click="executePrint">确认打印</Button>
          </div>
        </div>
      </Modal>

      <Modal v-model="importModalVisible" :title="importTitle" width="500" :footer-hide="true">
        <Upload :before-upload="handleImportFile" action="" :accept="'.xlsx'" type="drag">
          <div style="padding:30px 0">
            <Icon type="ios-cloud-upload" size="52" color="#3399ff" />
            <p>点击或拖拽 .xlsx 文件到此区域</p>
          </div>
        </Upload>
        <div v-if="importResult" style="margin-top:12px">
          <Alert v-if="importResult.success > 0" type="success">成功导入 {{ importResult.success }} 条</Alert>
          <Alert v-if="importResult.failed > 0" type="error">
            失败 {{ importResult.failed }} 条
            <div v-for="(e, i) in (importResult.errors || []).slice(0, 5)" :key="i" style="font-size:12px">
              第{{ e.row }}行: {{ e.error }}
            </div>
          </Alert>
        </div>
      </Modal>
    </template>
  </div>
</template>

<script>
import printingApi from '@/api/printing'
import CreateOrderPanel from '@/components/CreateOrderPanel.vue'

const STATUS_CONFIG = {
  Draft: { label: '草稿', color: 'default' },
  Predicted: { label: '已预报', color: 'blue' },
  WaitingShip: { label: '待发货', color: 'warning' },
  Shipped: { label: '已发货', color: 'purple' },
  Internet: { label: '已上网', color: 'success' },
  Sign: { label: '已签收', color: 'cyan' },
  Returned: { label: '已退货', color: 'error' },
  Problem: { label: '问题订单', color: 'error' }
}

const STATUS_FILTERS = [
  { key: '', statKey: 'total', label: '全部' },
  { key: 'Draft', statKey: 'Draft', label: '草稿' },
  { key: 'Predicted', statKey: 'Predicted', label: '已预报' },
  { key: 'WaitingShip', statKey: 'WaitingShip', label: '待发货' },
  { key: 'Shipped', statKey: 'Shipped', label: '已发货' },
  { key: 'Internet', statKey: 'Internet', label: '已上网' },
  { key: 'Sign', statKey: 'Sign', label: '已签收' },
  { key: 'Returned', statKey: 'Returned', label: '已退货' },
  { key: 'Problem', statKey: 'Problem', label: '问题订单' }
]

const DATE_TYPES = [
  { value: 'create', label: '制单日期' },
  { value: 'forecast', label: '预报时间' },
  { value: 'receipt', label: '收货时间' },
  { value: 'ship', label: '发货时间' },
  { value: 'internet', label: '上网时间' },
  { value: 'deliver', label: '签收时间' },
  { value: 'edit', label: '修改时间' }
]

const DEFAULT_VISIBLE_COLUMNS = [
  'customerNumber', 'status', 'waybillNumber', 'trackNumber', 'fbaNumber',
  'channelName', 'countryCode', 'pieces', 'forecastWeight', 'square',
  'inRweight', 'inMweight', 'inWeight', 'totalName', 'totalValue',
  'goodsType', 'isProblem', 'isBattery', 'isInsurance', 'consigneeName',
  'consigneeCity', 'consigneeProvince', 'createUsername', 'createDate'
]

const COLUMN_DEFS = [
  { key: 'customerNumber', title: '客户参考号', width: 180, type: 'link' },
  { key: 'status', title: '状态', width: 90, type: 'status' },
  { key: 'systemNumber', title: '系统单号', width: 140 },
  { key: 'waybillNumber', title: '运单号', width: 150 },
  { key: 'trackNumber', title: '转单号', width: 150 },
  { key: 'fbaNumber', title: 'FBA ID', width: 120 },
  { key: 'channelName', title: '收货渠道', width: 220 },
  { key: 'outChannelId', title: '渠道代码', width: 110 },
  { key: 'countryCode', title: '目的地', width: 80, align: 'center' },
  { key: 'pieces', title: '件数', width: 70, align: 'right', type: 'number' },
  { key: 'forecastWeight', title: '预报重', width: 90, align: 'right', type: 'number' },
  { key: 'square', title: '收货方数', width: 90, align: 'right', type: 'number' },
  { key: 'inRweight', title: '收货实重', width: 90, align: 'right', type: 'number' },
  { key: 'inMweight', title: '收货材重', width: 90, align: 'right', type: 'number' },
  { key: 'inWeight', title: '收货结算重', width: 110, align: 'right', type: 'number' },
  { key: 'totalName', title: '品名合计', width: 180, type: 'long' },
  { key: 'totalValue', title: '申报价值合计', width: 120, align: 'right', type: 'number' },
  { key: 'productType', title: '物品类别', width: 110 },
  { key: 'goodsType', title: '货物类型', width: 110 },
  { key: 'isProblem', title: '是否问题件', width: 100, align: 'center', type: 'booleanRed' },
  { key: 'isBattery', title: '是否带电', width: 90, align: 'center', type: 'booleanRed' },
  { key: 'isInsurance', title: '是否投保', width: 90, align: 'center', type: 'booleanRed' },
  { key: 'note', title: '运单备注', width: 180, type: 'long' },
  { key: 'otherNote', title: '其它备注', width: 180, type: 'long' },
  { key: 'consigneeName', title: '收件人名称', width: 130 },
  { key: 'consigneeAddress1', title: '收件人地址1', width: 220, type: 'long' },
  { key: 'consigneeAddress2', title: '收件人地址2', width: 220, type: 'long' },
  { key: 'consigneeZipcode', title: '收件人邮编', width: 120 },
  { key: 'consigneeCity', title: '收件人城市', width: 120 },
  { key: 'consigneeProvince', title: '收件人省州', width: 120 },
  { key: 'consigneeMobile', title: '收件人手机', width: 130 },
  { key: 'consigneeTel', title: '收件人电话', width: 130 },
  { key: 'receiptDate', title: '收货时间', width: 150, type: 'date' },
  { key: 'shipDate', title: '发货时间', width: 150, type: 'date' },
  { key: 'forecastDate', title: '预报时间', width: 150, type: 'date' },
  { key: 'createUsername', title: '制单人', width: 90 },
  { key: 'createDate', title: '制单日期', width: 150, type: 'date' },
  { key: 'editDate', title: '最后修改日期', width: 150, type: 'date' },
  { key: 'internetTime', title: '上网时间', width: 150, type: 'date' },
  { key: 'deliverDate', title: '签收时间', width: 150, type: 'date' },
  { key: 'sourceType', title: '来源类型', width: 110 }
]

const PRINT_ACTIONS = [
  { value: 'vendor_label', label: '代理标签', icon: 'md-print', silent: true },
  { value: 'receipt_label', label: '收货标签', icon: 'md-print', silent: true },
  { value: 'system_label', label: '内部标签', icon: 'md-print', silent: true },
  { value: 'system_vendor_label', label: '代理&内部标签', icon: 'md-print', silent: true },
  { value: 'invoice', label: '发票', icon: 'ios-paper-outline', silent: false },
  { value: 'express', label: '打印面单', icon: 'ios-paper-outline', silent: false },
  { value: 'platform_label', label: '平台面单', icon: 'ios-browsers-outline', silent: false },
  { value: 'download_label', label: '下载标签文件', icon: 'md-download', silent: false }
]

const PRINT_SETTINGS_KEY = 'printing:t6-print-settings'

const PRINT_ACTIONS_BY_VARIANT = {
  kdf: ['vendor_label', 'system_label'],
  dsf: ['system_label', 'vendor_label', 'system_vendor_label', 'express'],
  dw: ['system_vendor_label', 'express', 'receipt_label']
}

export default {
  name: 'Printing',
  components: { CreateOrderPanel },
  data() {
    return {
      channels: [],
      selectedChannel: '',
      channelStatus: '',
      syncLoading: false,
      ordersLoading: false,
      orders: [],
      selectedOrders: [],
      selectedStatus: '',
      searchQuery: '',
      showFilter: false,
      filterDateRange: [],
      dateType: 'create',
      filterDestination: '',
      filterCargoType: '',
      sortBy: 'createDate',
      sortOrder: 'desc',
      stats: {
        total: 0,
        Draft: 0,
        Predicted: 0,
        WaitingShip: 0,
        Shipped: 0,
        Internet: 0,
        Sign: 0,
        Returned: 0,
        Problem: 0
      },
      pagination: { page: 1, pageSize: 50, total: 0 },
      printModalVisible: false,
      printPkids: [],
      printAction: 'vendor_label',
      printDelivery: 'preview',
      silentPrinterName: '',
      silentCopies: 1,
      silentFallbackPreview: true,
      printChecking: false,
      printActions: PRINT_ACTIONS,
      importModalVisible: false,
      importType: 'import',
      importResult: null,
      visibleColumnKeys: this.loadVisibleColumns(),
      tableReady: false,
      tableHeight: 420,
      ordersLoaded: false,
      createMounted: false,
      listMounted: false,
      editingOrderId: null,
      editingOrderDetail: null,
      detailDrawerVisible: false,
      detailLoading: false,
      detailTab: 'basic',
      currentOrderRow: null,
      currentOrderDetail: null,
      statusFilters: STATUS_FILTERS,
      dateTypes: DATE_TYPES,
      destinations: [
        { value: 'AU', label: '澳大利亚' },
        { value: 'NZ', label: '新西兰' },
        { value: 'US', label: '美国' },
        { value: 'CA', label: '加拿大' },
        { value: 'GB', label: '英国' },
        { value: 'DE', label: '德国' },
        { value: 'JP', label: '日本' },
        { value: 'KR', label: '韩国' }
      ],
      cargoTypes: [
        { value: 'WPX', label: '普货' },
        { value: 'SPX', label: '敏感货' },
        { value: 'FPX', label: '食品' },
        { value: 'LPX', label: '液体' }
      ],
      detailDimensionColumns: [
        { title: '件数', key: 'qty', width: 90, align: 'right' },
        { title: '长(cm)', key: 'length', width: 100, align: 'right' },
        { title: '宽(cm)', key: 'width', width: 100, align: 'right' },
        { title: '高(cm)', key: 'height', width: 100, align: 'right' },
        { title: '单件实重(kg)', key: 'weight', minWidth: 130, align: 'right' }
      ],
      detailGoodsColumns: [
        { title: '商品SKU', key: 'sku', minWidth: 120 },
        { title: '英文名', key: 'enName', minWidth: 140 },
        { title: '中文名', key: 'cnName', minWidth: 140 },
        { title: '净重', key: 'netWeight', width: 100, align: 'right' },
        { title: '数量', key: 'qty', width: 90, align: 'right' },
        { title: '申报单价', key: 'unitPrice', width: 110, align: 'right' },
        { title: '申报总值', key: 'totalValue', width: 110, align: 'right' },
        { title: '币别', key: 'currency', width: 90 },
        { title: '海关编码', key: 'hsCode', minWidth: 130 },
        { title: '材质(英文)', key: 'material', minWidth: 140 },
        { title: '备注', key: 'remark', minWidth: 150 }
      ],
      detailAttachmentColumns: [
        { title: '名称', key: 'name', minWidth: 180 },
        { title: '类型', key: 'type', width: 120 },
        { title: '时间', key: 'time', width: 160 }
      ],
      detailTrackColumns: [
        { title: '时间', key: 'time', width: 170 },
        { title: '地点', key: 'location', width: 160 },
        { title: '轨迹内容', key: 'content', minWidth: 240 }
      ]
    }
  },
  computed: {
    isFormTab() {
      return this.$route.query.tab === 'create' || this.$route.query.tab === 'edit'
    },
    formMode() {
      return this.$route.query.tab === 'edit' ? 'edit' : 'create'
    },
    isEditTab() {
      return this.formMode === 'edit'
    },
    configurableColumns() {
      return COLUMN_DEFS
    },
    tableColumns() {
      const cols = [
        { type: 'selection', width: 42, align: 'center' }
      ]
      COLUMN_DEFS
        .filter(col => this.visibleColumnKeys.indexOf(col.key) >= 0)
        .forEach(col => cols.push(this.buildColumn(col)))
      cols.push({
        title: '操作',
        key: 'action',
        width: 160,
        align: 'center',
        render: (h, { row }) => h('div', { class: 'row-actions' }, [
          h('Button', {
            props: { type: 'text', size: 'small' },
            on: { click: () => this.viewOrder(row) }
          }, '查看'),
          h('Button', {
            props: { type: 'text', size: 'small', disabled: !this.canEditOrder(row) },
            on: { click: () => this.editOrder(row) }
          }, '编辑'),
          h('Button', {
            props: { type: 'text', size: 'small' },
            on: { click: () => this.printSingle(row) }
          }, '打印')
        ])
      })
      return cols
    },
    visibleOrders() {
      return this.tableReady ? this.orders : []
    },
    currentChannel() {
      return this.channels.find(ch => ch.channelCode === this.selectedChannel) || null
    },
    selectedChannelName() {
      const channel = this.currentChannel
      return channel ? channel.channelName : ''
    },
    availablePrintActions() {
      const channel = this.currentChannel
      let values = channel && Array.isArray(channel.supportedPrintActions) ? channel.supportedPrintActions : []
      if (!values.length) {
        const variant = this.detectPrintVariant(channel)
        values = PRINT_ACTIONS_BY_VARIANT[variant] || PRINT_ACTIONS.map(item => item.value)
      }
      const allowed = new Set(values)
      const actions = this.printActions.filter(item => allowed.has(item.value))
      return actions.length ? actions : this.printActions
    },
    hasSelection() {
      return this.selectedOrders.length > 0
    },
    hasActiveFilter() {
      return Boolean(
        this.searchQuery ||
        this.filterDestination ||
        this.filterCargoType ||
        (this.filterDateRange && this.filterDateRange.length && this.filterDateRange[0])
      )
    },
    importTitle() {
      if (this.importType === 'import-weight') return '导入重量'
      if (this.importType === 'import-modify') return '导入修改'
      return '运单导入'
    },
    selectedPrintActionConfig() {
      return this.printActions.find(item => item.value === this.printAction) || this.printActions[0]
    },
    printActionLabel() {
      return this.selectedPrintActionConfig ? this.selectedPrintActionConfig.label : '代理标签'
    },
    selectionSummary() {
      const pieces = this.selectedOrders.reduce((sum, row) => sum + Number(row.pieces || 0), 0)
      const weight = this.selectedOrders.reduce((sum, row) => sum + Number(row.forecastWeight || 0), 0)
      return `已选 ${this.selectedOrders.length} 条 / ${pieces} 件 / 预报重 ${this.formatNumber(weight)}`
    },
    pageSummary() {
      const pieces = this.orders.reduce((sum, row) => sum + Number(row.pieces || 0), 0)
      const weight = this.orders.reduce((sum, row) => sum + Number(row.forecastWeight || 0), 0)
      const value = this.orders.reduce((sum, row) => sum + Number(row.totalValue || 0), 0)
      return `当前页合计：${this.orders.length} 单，${pieces} 件，预报重 ${this.formatNumber(weight)}，申报价值 ${this.formatNumber(value)}`
    },
    detailSummary() {
      return (this.currentOrderDetail && this.currentOrderDetail.summary) || this.currentOrderRow || {}
    },
    detailWaybillInfo() {
      return (this.currentOrderDetail && this.currentOrderDetail.waybillInfo) || {}
    },
    detailConsignee() {
      return (this.currentOrderDetail && this.currentOrderDetail.consignee) || {}
    },
    detailSender() {
      return (this.currentOrderDetail && this.currentOrderDetail.sender) || {}
    },
    detailStatus() {
      return (this.currentOrderDetail && this.currentOrderDetail.status) || this.detailSummary.status || ''
    },
    detailTitle() {
      const ref = this.detailSummary.customerNumber || this.detailWaybillInfo.customerRef || this.detailSummary.systemNumber || ''
      return ref ? `订单详情 - ${ref}` : '订单详情'
    },
    canEditCurrentDetail() {
      if (this.currentOrderDetail && Object.prototype.hasOwnProperty.call(this.currentOrderDetail, 'editable')) {
        return !!this.currentOrderDetail.editable
      }
      return this.canEditOrder(this.currentOrderRow || {})
    },
    detailDimensions() {
      return (this.currentOrderDetail && this.currentOrderDetail.dimensions) || []
    },
    detailGoods() {
      return (this.currentOrderDetail && this.currentOrderDetail.goods) || []
    },
    detailAttachments() {
      return (this.currentOrderDetail && this.currentOrderDetail.attachments) || []
    },
    detailTracks() {
      return (this.currentOrderDetail && this.currentOrderDetail.tracks) || []
    },
    detailBasicGroups() {
      const summary = this.detailSummary
      const info = this.detailWaybillInfo
      const consignee = this.detailConsignee
      const sender = this.detailSender
      const goodsNames = this.detailGoods.map(item => item.cnName || item.enName).filter(Boolean).join(' / ')
      const declareTotal = this.detailGoods.reduce((sum, item) => sum + Number(item.totalValue || 0), 0)
      return [
        {
          title: '运单信息',
          items: [
            { label: '收货渠道', value: summary.channelName || info.channelId },
            { label: '目的地', value: info.destination || summary.countryCode },
            { label: '总件数', value: info.pieces || summary.pieces },
            { label: '预报实重', value: this.formatNumber(info.forecastWeight || summary.forecastWeight) },
            { label: '预报材重', value: this.formatNumber(info.forecastMWeight) },
            { label: '预报方数', value: this.formatNumber(info.forecastSquare || summary.square) },
            { label: '货物类型', value: this.cargoTypeLabel(info.cargoType || summary.goodsType) },
            { label: '申报币别', value: info.currency },
            { label: '是否投保', value: info.insured ? '是' : '否' },
            { label: '是否带电', value: info.hasBattery ? '是' : '否' },
            { label: '备注', value: info.note || summary.note }
          ]
        },
        {
          title: '收件信息',
          items: [
            { label: '名称', value: consignee.name || summary.consigneeName },
            { label: '电话', value: consignee.phone || summary.consigneeTel || summary.consigneeMobile },
            { label: '邮编', value: consignee.postcode || summary.consigneeZipcode },
            { label: '城市', value: consignee.city || summary.consigneeCity },
            { label: '省州', value: consignee.province || summary.consigneeProvince },
            { label: '地址1', value: consignee.address1 || summary.consigneeAddress1 },
            { label: '地址2', value: consignee.address2 || summary.consigneeAddress2 }
          ]
        },
        {
          title: '寄件信息',
          items: [
            { label: '名称', value: sender.name },
            { label: '公司', value: sender.company },
            { label: '电话', value: sender.phone },
            { label: '国家', value: sender.countryCode },
            { label: '邮编', value: sender.postcode },
            { label: '城市', value: sender.city },
            { label: '省州', value: sender.province },
            { label: '地址', value: sender.address1 }
          ]
        },
        {
          title: '物品信息',
          items: [
            { label: '品名合计', value: goodsNames || summary.totalName },
            { label: '申报价值合计', value: this.formatNumber(declareTotal || summary.totalValue) },
            { label: '材质', value: this.detailGoods.map(item => item.material).filter(Boolean).join(' / ') },
            { label: 'HS Code', value: this.detailGoods.map(item => item.hsCode).filter(Boolean).join(' / ') }
          ]
        }
      ]
    }
  },
  watch: {
    isFormTab(val) {
      if (val) {
        this.createMounted = true
        this.tableReady = false
        if (!this.isEditTab) {
          this.editingOrderId = null
          this.editingOrderDetail = null
        }
        return
      }
      this.activateList()
    },
    '$route.query.tab'(val) {
      if (val === 'edit' && this.$route.query.orderId) {
        this.createMounted = true
        this.editingOrderId = Number(this.$route.query.orderId)
      }
      if (val === 'create') {
        this.editingOrderId = null
        this.editingOrderDetail = null
      }
    },
    '$route.query.orderId'(val) {
      if (this.isEditTab && val) {
        this.editingOrderId = Number(val)
      }
    },
    visibleColumnKeys: {
      deep: true,
      handler(val) {
        localStorage.setItem('printing:t6-list-columns', JSON.stringify(val))
      }
    },
    printAction() {
      if (this.selectedPrintActionConfig && !this.selectedPrintActionConfig.silent && this.printDelivery === 'silent') {
        this.printDelivery = 'preview'
      }
      this.savePrintSettings()
    },
    printDelivery() {
      this.savePrintSettings()
    },
    silentPrinterName() {
      this.savePrintSettings()
    },
    silentCopies() {
      this.savePrintSettings()
    },
    silentFallbackPreview() {
      this.savePrintSettings()
    },
    selectedChannel() {
      this.printAction = this.normalizePrintAction(this.printAction)
    },
    showFilter() {
      this.scheduleTableRender()
    }
  },
  async created() {
    this.createMounted = this.isFormTab
    this.listMounted = !this.isFormTab
    if (this.isEditTab && this.$route.query.orderId) {
      this.editingOrderId = Number(this.$route.query.orderId)
    }
    this.loadPrintSettings()
    await this.loadChannels()
    if (!this.isFormTab) {
      this.activateList()
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateTableHeight)
    this.updateTableHeight()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
  },
  methods: {
    detectPrintVariant(channel) {
      if (!channel) return 'generic'
      const code = String(channel.channelCode || '').toUpperCase()
      const url = String(channel.printUrl || channel.trackUrl || '').toLowerCase()
      if (channel.printVariant) return String(channel.printVariant).toLowerCase()
      if (code === 'KDF' || url.indexOf('kdf.t6soft.com') >= 0) return 'kdf'
      if (code === 'DSF' || url.indexOf('dsfexpress.t6soft.com') >= 0) return 'dsf'
      if (code === 'DW' || url.indexOf('203.195.161.123:8180') >= 0 || url.indexOf('dw.spidertrans.cn') >= 0) return 'dw'
      return 'generic'
    },
    firstAvailablePrintAction() {
      return (this.availablePrintActions[0] && this.availablePrintActions[0].value) || 'vendor_label'
    },
    normalizePrintAction(action) {
      const desired = action || this.printAction || 'vendor_label'
      if (this.availablePrintActions.some(item => item.value === desired)) return desired
      return this.firstAvailablePrintAction()
    },
    loadPrintSettings() {
      try {
        const saved = JSON.parse(localStorage.getItem(PRINT_SETTINGS_KEY) || '{}')
        if (saved.printAction && this.printActions.some(item => item.value === saved.printAction)) {
          this.printAction = saved.printAction
        }
        if (saved.printDelivery === 'preview' || saved.printDelivery === 'silent') {
          this.printDelivery = saved.printDelivery
        }
        this.silentPrinterName = saved.silentPrinterName || ''
        this.silentCopies = Number(saved.silentCopies || 1)
        this.silentFallbackPreview = saved.silentFallbackPreview !== false
      } catch (e) {}
      this.printAction = this.normalizePrintAction(this.printAction)
    },
    savePrintSettings() {
      const settings = {
        printAction: this.printAction,
        printDelivery: this.printDelivery,
        silentPrinterName: this.silentPrinterName,
        silentCopies: this.silentCopies,
        silentFallbackPreview: this.silentFallbackPreview
      }
      localStorage.setItem(PRINT_SETTINGS_KEY, JSON.stringify(settings))
    },
    loadVisibleColumns() {
      try {
        const saved = JSON.parse(localStorage.getItem('printing:t6-list-columns') || 'null')
        if (Array.isArray(saved) && saved.length) return saved
      } catch (e) {}
      return DEFAULT_VISIBLE_COLUMNS.slice()
    },
    resetColumns() {
      this.visibleColumnKeys = DEFAULT_VISIBLE_COLUMNS.slice()
    },
    buildColumn(col) {
      const column = {
        title: col.title,
        key: col.key,
        width: col.width,
        minWidth: col.minWidth,
        align: col.align || 'left',
        fixed: col.fixed,
        sortable: 'custom'
      }
      column.render = (h, { row }) => this.renderCell(h, row, col)
      return column
    },
    renderCell(h, row, col) {
      if (col.type === 'status') {
        const cfg = STATUS_CONFIG[row.status] || { label: row.status || '未知', color: 'default' }
        return h('Tag', { props: { color: cfg.color } }, cfg.label)
      }
      if (col.type === 'booleanRed') {
        return h('span', { class: row[col.key] ? 'bool-yes' : 'bool-no' }, row[col.key] ? '是' : '否')
      }
      if (col.type === 'date') {
        return h('span', this.formatDateTime(row[col.key]))
      }
      if (col.type === 'number') {
        return h('span', this.formatNumber(row[col.key]))
      }
      if (col.type === 'link') {
        return h('span', {
          class: 'cell-link',
          on: { click: () => this.viewOrder(row) }
        }, row[col.key] || '-')
      }
      const text = row[col.key] || '-'
      return h('span', {
        class: col.type === 'long' ? 'cell-long' : '',
        attrs: { title: text }
      }, text)
    },
    statusLabel(s) {
      return (STATUS_CONFIG[s] || {}).label || s || '未知'
    },
    statusColor(s) {
      return (STATUS_CONFIG[s] || {}).color || 'default'
    },
    cargoTypeLabel(value) {
      const text = String(value || '').trim()
      const key = text.toLowerCase()
      const map = {
        general: '普货',
        wpx: '普货',
        '普货': '普货',
        '普通货': '普货',
        '包裹': '普货',
        sensitive: '敏感货',
        spx: '敏感货',
        '敏感': '敏感货',
        '敏感货': '敏感货',
        food: '食品',
        fpx: '食品',
        '食品': '食品',
        liquid: '液体',
        lpx: '液体',
        '液体': '液体'
      }
      return map[key] || map[text] || text
    },
    displayValue(value) {
      if (value === 0) return '0'
      if (value === false) return '否'
      if (value === true) return '是'
      return value || '-'
    },
    formatNumber(value) {
      const n = Number(value || 0)
      return Number.isFinite(n) ? n.toFixed(2).replace(/\.00$/, '') : '0'
    },
    formatDateTime(value) {
      if (!value) return '-'
      const text = String(value).replace('T', ' ').replace(/\.\d+.*$/, '')
      return text.length > 19 ? text.slice(0, 19) : text
    },
    formatDateParam(d) {
      if (!d) return ''
      const dt = new Date(d)
      const y = dt.getFullYear()
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const day = String(dt.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    canEditOrder(row) {
      return row.status === 'Draft' || row.status === 'Predicted'
    },
    rowClassName(row) {
      if (row.isProblem || row.status === 'Problem') return 'problem-row'
      return ''
    },
    async loadChannels() {
      try {
        const res = await printingApi.listChannels()
        this.channels = res.items || []
        const saved = localStorage.getItem('printing:selectedChannel')
        if (saved && this.channels.some(ch => ch.channelCode === saved)) {
          this.selectedChannel = saved
        }
        this.printAction = this.normalizePrintAction(this.printAction)
      } catch (e) {
        this.$Message.error('加载渠道失败')
      }
    },
    activateList() {
      this.listMounted = true
      this.scheduleTableRender()
      if (this.selectedChannel && !this.ordersLoaded) {
        this.activateSelectedChannel()
      }
    },
    scheduleTableRender() {
      this.tableReady = false
      this.$nextTick(() => {
        const render = () => {
          this.updateTableHeight()
          this.tableReady = true
        }
        if (window.requestAnimationFrame) {
          window.requestAnimationFrame(() => window.requestAnimationFrame(render))
        } else {
          setTimeout(render, 0)
        }
      })
    },
    updateTableHeight() {
      if (this.isFormTab || !this.selectedChannel) return
      this.$nextTick(() => {
        const shell = this.$refs.tableShell
        const footer = this.$refs.tableFooter
        if (!shell) return
        const shellRect = shell.getBoundingClientRect()
        const footerHeight = footer ? footer.offsetHeight : 50
        const available = window.innerHeight - shellRect.top - footerHeight - 10
        this.tableHeight = Math.max(260, Math.floor(available))
      })
    },
    async activateSelectedChannel() {
      if (!this.selectedChannel) return
      this.channelStatus = 'loading'
      try {
        await printingApi.channelLogin(this.selectedChannel)
        this.channelStatus = 'active'
      } catch (e) {
        this.channelStatus = 'inactive'
        this.$Message.warning((e.response && e.response.data && e.response.data.detail) || '渠道登录失败，部分功能可能不可用')
      }
      await this.refreshAll()
    },
    async onChannelChange(code) {
      this.selectedOrders = []
      this.ordersLoaded = false
      if (!code) {
        this.channelStatus = ''
        this.orders = []
        localStorage.removeItem('printing:selectedChannel')
        return
      }
      localStorage.setItem('printing:selectedChannel', code)
      this.pagination.page = 1
      await this.activateSelectedChannel()
    },
    currentParams(includePaging = true) {
      const params = {
        channel_code: this.selectedChannel,
        status: this.selectedStatus || undefined,
        search: this.searchQuery || undefined,
        destination: this.filterDestination || undefined,
        cargo_type: this.filterCargoType || undefined,
        date_type: this.dateType || 'create',
        sort_by: this.sortBy || undefined,
        sort_order: this.sortOrder || undefined
      }
      if (includePaging) {
        params.page = this.pagination.page
        params.page_size = this.pagination.pageSize
      }
      if (this.filterDateRange && this.filterDateRange.length === 2 && this.filterDateRange[0]) {
        params.date_start = this.formatDateParam(this.filterDateRange[0])
        params.date_end = this.formatDateParam(this.filterDateRange[1])
      }
      return params
    },
    async refreshAll() {
      await this.loadStats()
      await this.loadOrders()
    },
    async loadStats() {
      if (!this.selectedChannel) return
      try {
        const res = await printingApi.getStats({ channel_code: this.selectedChannel })
        this.stats = { ...this.stats, ...res }
      } catch (e) {}
    },
    async loadOrders() {
      if (!this.selectedChannel) return
      this.ordersLoading = true
      try {
        const res = await printingApi.listOrders(this.currentParams(true))
        this.orders = res.items || []
        this.pagination.total = res.total || 0
        this.selectedOrders = []
        this.ordersLoaded = true
        this.scheduleTableRender()
      } catch (e) {
        this.$Message.error('加载订单失败')
      } finally {
        this.ordersLoading = false
      }
    },
    searchOrders() {
      this.pagination.page = 1
      this.loadOrders()
    },
    selectStatus(status) {
      this.selectedStatus = status
      this.pagination.page = 1
      this.loadOrders()
    },
    onSortChange({ key, order }) {
      if (!order || order === 'normal') {
        this.sortBy = 'createDate'
        this.sortOrder = 'desc'
      } else {
        this.sortBy = key
        this.sortOrder = order
      }
      this.pagination.page = 1
      this.loadOrders()
    },
    onPageChange(p) {
      this.pagination.page = p
      this.loadOrders()
    },
    onPageSizeChange(s) {
      this.pagination.pageSize = s
      this.pagination.page = 1
      this.loadOrders()
    },
    onSelectionChange(selection) {
      this.selectedOrders = selection
    },
    resetFilters() {
      this.filterDateRange = []
      this.filterDestination = ''
      this.filterCargoType = ''
      this.searchQuery = ''
      this.dateType = 'create'
      this.pagination.page = 1
      this.loadOrders()
    },
    ensureSelection(label) {
      if (this.hasSelection) return true
      this.$Message.warning(`请先勾选需要${label}的运单`)
      return false
    },
    handleToolbar(name) {
      if (name && name.indexOf('print-') === 0) {
        this.handlePrint(name.replace(/^print-/, ''))
        return
      }
      const actions = {
        'import': () => this.openImport('import'),
        'import-modify': () => this.openImport('import-modify'),
        'import-weight': () => this.openImport('import-weight'),
        'export-selected': () => this.handleExport(false),
        'export-filtered': () => this.handleExport(true),
        'copy': () => this.handleCopy(),
        'delete': () => this.handleDelete()
      }
      const fn = actions[name]
      if (fn) fn()
    },
    async handleSync() {
      if (!this.selectedChannel) return
      this.syncLoading = true
      try {
        const res = await printingApi.syncOrders({
          channel_code: this.selectedChannel,
          status: this.selectedStatus || '',
          max_pages: 50
        })
        this.$Message.success(`同步完成，共更新 ${res.saved || 0} 条`)
        await this.refreshAll()
      } catch (e) {
        this.$Message.error((e.response && e.response.data && e.response.data.detail) || '同步失败')
      } finally {
        this.syncLoading = false
      }
    },
    openCreateOrder() {
      this.createMounted = true
      this.editingOrderId = null
      this.editingOrderDetail = null
      this.$router.push({ path: '/printing', query: { tab: 'create' } })
    },
    async loadOrderDetail(row) {
      if (!row || !row.id) return null
      this.currentOrderRow = row
      try {
        const detail = await printingApi.getOrder(row.id)
        this.currentOrderDetail = detail
        if (detail.summary && detail.summary.channelCode && detail.summary.channelCode !== this.selectedChannel) {
          this.selectedChannel = detail.summary.channelCode
          localStorage.setItem('printing:selectedChannel', detail.summary.channelCode)
        }
        return detail
      } catch (e) {
        this.$Message.error((e.response && e.response.data && e.response.data.detail) || '加载订单详情失败')
        return null
      }
    },
    async editOrder(row) {
      if (!this.canEditOrder(row)) {
        this.$Message.warning('只有草稿或已预报订单可以修改')
        return
      }
      this.detailLoading = true
      const detail = await this.loadOrderDetail(row)
      this.detailLoading = false
      if (detail) {
        this.openEditOrder(detail, row)
      }
    },
    async viewOrder(row) {
      this.detailDrawerVisible = true
      this.detailTab = 'basic'
      this.detailLoading = true
      await this.loadOrderDetail(row)
      this.detailLoading = false
    },
    openEditOrder(detail, row) {
      const target = detail || this.currentOrderDetail
      const fallback = row || this.currentOrderRow || {}
      const id = (target && target.id) || fallback.id
      if (!id) {
        this.$Message.error('缺少订单ID，无法进入修改页面')
        return
      }
      this.createMounted = true
      this.editingOrderId = id
      this.editingOrderDetail = target || null
      this.detailDrawerVisible = false
      this.$router.push({ path: '/printing', query: { tab: 'edit', orderId: id } })
    },
    startEditFromDetail() {
      if (!this.canEditCurrentDetail) {
        this.$Message.warning('只有草稿或已预报订单可以修改')
        return
      }
      this.openEditOrder(this.currentOrderDetail, this.currentOrderRow)
    },
    handleExcelModify() {
      this.$Message.info('批量导入修改后续接入')
    },
    onFormSaved(channelCode, result, options = {}) {
      if (channelCode) {
        this.selectedChannel = channelCode
        localStorage.setItem('printing:selectedChannel', channelCode)
      }
      if (options.closeAfterSave || options.mode === 'edit') {
        this.goOrderList()
      }
      this.refreshAll()
    },
    goOrderList() {
      if (this.isFormTab) {
        this.$router.push({ path: '/printing' })
      } else {
        this.activateList()
      }
    },
    handleExport(allFiltered) {
      if (!allFiltered && !this.ensureSelection('导出')) return
      const ids = allFiltered ? '' : this.selectedOrders.map(o => o.id).join(',')
      const url = printingApi.exportOrdersUrl({
        ...this.currentParams(false),
        ids: ids || undefined,
        format: 'xlsx'
      })
      window.open(url)
    },
    async handleCopy() {
      if (!this.ensureSelection('复制')) return
      try {
        const res = await printingApi.copyOrders(this.selectedOrders.map(o => o.id))
        this.$Message.success(res.message || '复制成功')
        await this.refreshAll()
      } catch (e) {
        this.$Message.error('复制失败')
      }
    },
    handleDelete() {
      if (!this.ensureSelection('删除')) return
      const ids = this.selectedOrders.map(o => o.id)
      this.$Modal.confirm({
        title: '确认删除',
        content: `确定删除选中的 ${ids.length} 条本地缓存？（不会删除 T6 系统中的订单）`,
        onOk: async () => {
          try {
            await printingApi.deleteOrders(ids)
            this.$Message.success('删除成功')
            await this.refreshAll()
          } catch (e) {
            this.$Message.error('删除失败')
          }
        }
      })
    },
    handlePrint(action) {
      if (!this.ensureSelection('打印')) return
      this.openPrintDialog(this.selectedOrders, action || 'vendor_label')
    },
    openPrintDialog(rows, action) {
      const nextAction = this.normalizePrintAction(action || 'vendor_label')
      if (action && nextAction !== action) {
        this.$Message.warning('当前渠道不支持该打印项，已切换到可用打印项')
      }
      this.printAction = nextAction
      this.printPkids = (rows || []).map(o => o && o.pkid).filter(Boolean)
      if (!this.printPkids.length) {
        this.$Message.warning('选中的运单缺少T6 pkid，无法打印')
        return
      }
      this.printModalVisible = true
    },
    printSingle(row) {
      this.openPrintDialog([row], 'vendor_label')
    },
    printUrl(action, forcePdf = false) {
      return printingApi.printLabelUrl({
        channel_code: this.selectedChannel,
        pkids: this.printPkids.join(','),
        print_action: action || this.printAction,
        force_pdf: forcePdf ? '1' : undefined
      })
    },
    absolutePrintUrl(action, forcePdf = false) {
      const url = this.printUrl(action, forcePdf)
      if (/^https?:\/\//i.test(url)) return url
      return `${window.location.origin}${url}`
    },
    async executePrint() {
      if (!this.printPkids.length) return
      if (this.printDelivery === 'silent' && !this.silentPrinterName) {
        this.$Message.warning('请先填写本机打印机名称')
        return
      }
      this.printChecking = true
      try {
        const check = await printingApi.checkPrintLabel({
          channel_code: this.selectedChannel,
          pkids: this.printPkids,
          print_action: this.printAction,
          delivery: this.printDelivery
        })
        if (check && check.success === false) {
          this.$Message.error(check.message || check.msg || '打印检查失败')
          return
        }
        if (this.printDelivery === 'silent' && check && check.canSilent === false) {
          this.$Message.warning(check.message || '该打印内容不支持静默打印，已切换为浏览器预览')
          this.openPreview(this.printAction)
          this.printModalVisible = false
          return
        }
      } catch (e) {
        this.$Message.error((e.response && e.response.data && e.response.data.detail) || '打印检查失败')
        return
      } finally {
        this.printChecking = false
      }
      if (this.printDelivery === 'silent') {
        await this.executeSilentPrint()
      } else {
        this.openPreview(this.printAction)
      }
      this.printModalVisible = false
    },
    openPreview(action) {
      const url = this.printUrl(action || this.printAction, false)
      window.open(url)
    },
    async executeSilentPrint() {
      const pdfUrl = this.absolutePrintUrl(this.printAction, true)
      try {
        for (let i = 0; i < Number(this.silentCopies || 1); i += 1) {
          await this.callLocalPrintPlugin(pdfUrl, this.silentPrinterName)
        }
        this.$Message.success('已发送到本机打印插件')
      } catch (e) {
        this.$Message.error(e && e.message ? e.message : '打印失败：请检查是否安装本机打印插件')
        if (this.silentFallbackPreview) {
          this.openPreview(this.printAction)
        }
      }
    },
    callLocalPrintPlugin(fileName, printerName) {
      return new Promise((resolve, reject) => {
        const callbackName = `__t6PrintCallback_${Date.now()}_${Math.floor(Math.random() * 10000)}`
        const script = document.createElement('script')
        const cleanup = () => {
          clearTimeout(timer)
          if (script.parentNode) script.parentNode.removeChild(script)
          try { delete window[callbackName] } catch (e) { window[callbackName] = undefined }
        }
        const timer = setTimeout(() => {
          cleanup()
          reject(new Error('打印失败：请检查是否安装本机打印插件'))
        }, 15000)
        window[callbackName] = () => {
          cleanup()
          resolve()
        }
        script.onerror = () => {
          cleanup()
          reject(new Error('打印失败：请检查是否安装本机打印插件'))
        }
        const qs = new URLSearchParams({
          fileName,
          printername: printerName,
          jsoncallback: callbackName
        })
        script.src = `http://localhost:8866/AjaxService.svc/PrintPDFbyname?${qs.toString()}`
        document.body.appendChild(script)
      })
    },
    openImport(type) {
      this.importType = type
      this.importResult = null
      this.importModalVisible = true
    },
    async handleImportFile(file) {
      try {
        let res
        if (this.importType === 'import-weight') {
          res = await printingApi.importWeight(this.selectedChannel, file)
          this.importResult = { success: res.updated, failed: res.notFound, errors: [] }
        } else if (this.importType === 'import-modify') {
          res = await printingApi.importModify(this.selectedChannel, file)
          this.importResult = { success: res.updated, failed: res.notFound, errors: [] }
        } else {
          res = await printingApi.importOrders(this.selectedChannel, this.selectedStatus || 'Draft', file)
          this.importResult = res
        }
        this.$Message.success('导入完成')
        await this.refreshAll()
      } catch (e) {
        this.$Message.error((e.response && e.response.data && e.response.data.detail) || '导入失败')
      }
      return false
    }
  }
}
</script>

<style scoped>
.printing-page {
  height: calc(100vh - 64px);
  padding: 10px;
  overflow: hidden;
  background: #eef3f8;
}
.printing-page-create {
  height: auto;
  min-height: 100%;
  padding: 0;
  overflow: visible;
  background: #fff;
}
.t6-list-page {
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.t6-channel-bar {
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #d7e2ee;
  border-radius: 4px 4px 0 0;
}
.channel-left,
.channel-right,
.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.channel-name,
.sync-time,
.selection-info,
.cal-info {
  color: #60758a;
  font-size: 12px;
  white-space: nowrap;
}
.status-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  background: #f7fbff;
  border: 1px solid #d7e2ee;
  border-top: none;
  padding: 6px 8px;
}
.status-pill {
  height: 28px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: #2d4054;
  padding: 0 12px;
  cursor: pointer;
  font-size: 13px;
}
.status-pill:hover,
.status-pill.active {
  color: #0f6fb7;
  border-color: #a8d2f0;
  background: #fff;
}
.status-pill b {
  font-weight: 600;
  color: #1f3b57;
}
.t6-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 42px;
  padding: 6px 8px;
  background: #fff;
  border-left: 1px solid #d7e2ee;
  border-right: 1px solid #d7e2ee;
}
.quick-search {
  width: 300px;
}
.filter-panel {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #fbfdff;
  border: 1px solid #d7e2ee;
  border-bottom: none;
}
.table-shell {
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #d7e2ee;
  overflow: hidden;
}
.table-footer {
  flex-shrink: 0;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  overflow-x: auto;
  background: #fff;
  border-top: 1px solid #e5edf5;
  box-shadow: 0 -2px 8px rgba(31, 59, 87, 0.06);
}
.column-pop {
  max-height: 360px;
  overflow: auto;
}
.column-pop-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 600;
}
.column-check-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 10px;
}
.cell-link {
  color: #0f6fb7;
  cursor: pointer;
}
.cell-long {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.bool-yes {
  color: #ed4014;
}
.bool-no {
  color: #515a6e;
}
.empty-state {
  text-align: center;
  padding: 90px 20px;
  color: #7b8da0;
  background: #fff;
  border: 1px solid #d7e2ee;
}
.empty-state p {
  margin-top: 12px;
}
.print-type-options {
  padding: 10px 0;
}
.row-actions {
  display: flex;
  justify-content: center;
  gap: 2px;
}
.detail-drawer-body {
  position: relative;
  min-height: calc(100vh - 150px);
  padding-bottom: 58px;
}
.detail-summary-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin-bottom: 12px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e8eaec;
  border-radius: 4px;
  color: #515a6e;
}
.detail-groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.detail-group {
  border: 1px solid #e8eaec;
  border-radius: 4px;
  background: #fff;
}
.detail-group h4 {
  margin: 0;
  padding: 9px 12px;
  font-size: 14px;
  color: #17233d;
  background: #f8fafc;
  border-bottom: 1px solid #e8eaec;
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.detail-item {
  display: grid;
  grid-template-columns: 98px minmax(0, 1fr);
  min-height: 38px;
  border-right: 1px solid #eef0f3;
  border-bottom: 1px solid #eef0f3;
}
.detail-item label {
  padding: 9px 10px;
  color: #808695;
  background: #fbfcfd;
}
.detail-item span {
  min-width: 0;
  padding: 9px 10px;
  color: #17233d;
  overflow-wrap: anywhere;
}
.detail-empty {
  padding: 80px 20px;
  text-align: center;
  color: #808695;
  border: 1px dashed #d7dde4;
  border-radius: 4px;
  background: #fbfcfd;
}
.detail-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 16px;
  background: #fff;
  border-top: 1px solid #e8eaec;
}
.print-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.print-row {
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
}
.print-row label {
  color: #515a6e;
  text-align: right;
}
.print-row.compact {
  grid-template-columns: 88px 120px minmax(0, 1fr);
}
.silent-settings {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid #e8eaec;
  background: #fbfcfd;
}
.print-summary {
  padding: 8px 10px;
  color: #60758a;
  background: #f7fbff;
  border: 1px solid #d7e2ee;
}
.print-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.printing-page >>> .ivu-table .problem-row td {
  color: #ed4014;
  background: #fff7f5;
}
.printing-page >>> .ivu-table-wrapper {
  min-height: 0;
  flex: 1;
}
.printing-page >>> .ivu-table {
  overflow: hidden;
}
.printing-page >>> .ivu-table-body,
.printing-page >>> .ivu-table-overflowX {
  overscroll-behavior: contain;
}
.printing-page >>> .ivu-table-cell {
  padding-left: 8px;
  padding-right: 8px;
}
.printing-page >>> .ivu-btn-small {
  padding: 1px 8px;
}
@media (max-width: 900px) {
  .t6-channel-bar,
  .t6-toolbar,
  .table-footer {
    align-items: stretch;
    flex-direction: column;
  }
  .toolbar-left,
  .toolbar-right {
    flex-wrap: wrap;
  }
  .quick-search {
    width: 100%;
  }
  .detail-grid {
    grid-template-columns: 1fr;
  }
  .print-row,
  .print-row.compact {
    grid-template-columns: 1fr;
  }
  .print-row label {
    text-align: left;
  }
}
</style>
